<template>
  <div class="personality-selector">
    <v-container>
      <v-row class="mb-4">
        <v-col cols="12">
          <v-alert
            type="warning"
            outlined
          >
            <h3 class="headline mb-2">Podcast Personality Selector</h3>
            <p>Choose a personality style for your AI-generated podcast. Each style will affect how the experts communicate while maintaining the podcast format.</p>
            <v-tooltip right max-width="400">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  text
                  small
                  v-bind="attrs"
                  v-on="on"
                  color="warning"
                >
                  Learn More
                </v-btn>
              </template>
              <v-card class="pa-4">
                <h3 class="headline mb-2">Podcast Style Information</h3>
                <v-divider class="mb-3"></v-divider>
                <p class="body-2 mb-2">Understanding podcast styles:</p>
                <ul class="body-2 pl-4 mb-3">
                  <li><strong>Purpose:</strong> To provide varied podcast experiences</li>
                  <li><strong>Impact:</strong> Influences how experts deliver information</li>
                  <li><strong>Structure:</strong> The podcast format remains consistent</li>
                </ul>
                <p class="body-2 mb-2"><strong>Important Notes:</strong></p>
                <ul class="body-2 pl-4">
                  <li>All podcast styles maintain expert discussion format</li>
                  <li>Information accuracy is preserved regardless of style</li>
                  <li>For more creative styles, consider using a higher temperature setting</li>
                </ul>
                <v-divider class="my-3"></v-divider>
                <p class="caption font-italic">
                  Choose a style that enhances your conversation experience.
                </p>
              </v-card>
            </v-tooltip>
          </v-alert>
        </v-col>
      </v-row>
      <v-row>
        <v-col v-for="personality in personalityPrompts" :key="personality.name" cols="12" sm="6" md="4">
          <v-card
            :class="{ 'selected': selectedPersonality === personality.name }"
            @click="selectPersonality(personality)"
            hover
            class="d-flex flex-column personality-card"
          >
            <v-card-title class="d-flex align-center">
              <v-icon left :color="getIconColor(personality.name)" class="mr-2">{{ getIcon(personality.name) }}</v-icon>
              <span class="personality-title">{{ personality.name }}</span>
            </v-card-title>
            <v-card-text class="flex-grow-1">{{ personality.description }}</v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <v-btn
      @click="setPrompt"
      color="primary"
      class="mt-4 black--text"
      :disabled="!selectedPersonality"
    >
      Set Prompt
    </v-btn>
  </div>
</template>

<script>
const personalityPrompts = [
  {
    name: "Sarcastic Sage",
    description: "Utterly sarcastic and cynical experts",
    prompt: "You are creating a podcast where all experts maintain their expertise but deliver content with deep sarcasm and cynicism. They should use irony liberally, include witty cynical observations, maintain a slightly jaded perspective, and occasionally sigh or make comments about 'having to explain this again.' Despite the sarcasm, all information should remain accurate."
  },
  {
    name: "Authoritarian Analyst",
    description: "Strict and no-nonsense discussion",
    prompt: "You are creating a podcast where all experts speak with authority and directness. They should use declarative confident statements, minimize hedging language, present information as settled fact when appropriate, and occasionally correct each other firmly but professionally. The conversation should have a strict, disciplined flow."
  },
  {
    name: "Comedy King",
    description: "Humorous and joke-filled podcast",
    prompt: "You are creating a podcast where all experts incorporate humor while maintaining their expertise. They should include jokes, puns, and witty observations throughout, use humorous analogies to explain complex concepts, engage in friendly banter, and develop at least 3 running gags that recur throughout. Despite the humor, all information should remain accurate."
  },
  {
    name: "Poetic Philosopher",
    description: "Experts use poetic language and metaphors",
    prompt: "You are creating a podcast where all experts speak poetically and philosophically. They should use metaphors and analogies frequently, incorporate poetic language and occasional rhymes, draw connections to nature, literature, and philosophical concepts, and occasionally speak in verse for brief sections."
  },
  {
    name: "Tech Enthusiast",
    description: "Technology-focused excitement throughout",
    prompt: "You are creating a podcast where all experts express excitement about technology. They should use tech jargon and analogies where appropriate, express enthusiasm about technological aspects of any topic, make connections to tech trends even for non-tech subjects, and occasionally reference famous tech innovations or companies."
  },
];

export default {
  data() {
    return {
      selectedPersonality: '',
      personalityPrompts
    };
  },
  methods: {
    selectPersonality(personality) {
      this.selectedPersonality = personality.name;
    },
    setPrompt() {
      if (this.selectedPersonality) {
        const selectedPrompt = this.personalityPrompts.find(p => p.name === this.selectedPersonality);
        if (selectedPrompt) {
          this.$emit('prompt-set', selectedPrompt.prompt);
        }
      }
    },
    getIcon(personalityName) {
      const icons = {
        "Standard Podcast": "mdi-podcast",
        "Sarcastic Sage": "mdi-emoticon-devil",
        "Authoritarian Analyst": "mdi-account-tie",
        "Comedy King": "mdi-emoticon-excited",
        "Poetic Philosopher": "mdi-feather",
        "Tech Enthusiast": "mdi-laptop",
        "Historical Narrators": "mdi-book-open-page-variant",
        "Movie Critics": "mdi-movie-open",
        "Debate Champions": "mdi-gavel",
        "Space Explorers": "mdi-rocket",
        "Tutorial Guides": "mdi-teach",
        "Sports Commentators": "mdi-trophy",
        "Financial Analysts": "mdi-chart-line"
      };
      return icons[personalityName] || "mdi-account";
    },
    getIconColor(personalityName) {
      const colors = {
        "Standard Podcast": "blue-grey",
        "Sarcastic Sage": "red",
        "Authoritarian Analyst": "blue",
        "Comedy King": "yellow darken-2",
        "Poetic Philosopher": "purple",
        "Tech Enthusiast": "green",
        "Historical Narrators": "brown",
        "Movie Critics": "deep-orange",
        "Debate Champions": "indigo",
        "Space Explorers": "deep-purple",
        "Tutorial Guides": "teal",
        "Sports Commentators": "amber darken-2",
        "Financial Analysts": "green darken-3"
      };
      return colors[personalityName] || "grey";
    }
  }
};
</script>

<style scoped>
.personality-selector .v-card {
  transition: all 0.3s ease;
  cursor: pointer;
  height: 100%;
  border: 1px solid gray;
}

.personality-selector .v-card.selected {
  border: 2px solid var(--v-primary-base);
  transform: translateY(-5px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
}

.personality-selector .v-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.personality-card {
  display: flex;
  flex-direction: column;
  min-height: 150px; /* Set a minimum height for all cards */
}

.personality-title {
  font-size: 16px;
  line-height: 1.2;
  word-break: break-word;
}

.v-card-text {
  padding-top: 8px;
  font-size: 14px;
  line-height: 1.4;
}

/* Make sure we have consistent spacing for icons */
.v-icon.mr-2 {
  margin-right: 8px !important;
}
</style>
